import React, { useState, useEffect, memo } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
// import Spinner from "../spinner/modalSpinner";
import ErrorMessage from "../../../../components/error/ErrorComponent";

import Geocode from "react-geocode";
import { GoogleMap, useJsApiLoader, MarkerF } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import InputTextComponent from "../../../../components/forms/InputTextComponent";

const configurationManager = require("../../../../utils/config/configurationManager");
const stringUtil = require("../../../../utils/string/stringUtil");
const viewUtil = require("../../../../utils/view/viewUtil");
const rmsApiProxyRetry = require("../../../../utils/api/rmsApiProxyRetry");
const guidUtil = require("../../../../utils/guid/guidUtil");
const localizationUtils = require("../../../../utils/domain/localizationUtils");
const translator = require("../../../../utils/translator/translator");

const googleMapsApiKey = configurationManager.getConfig().googleMapsApiKey;
Geocode.setApiKey(googleMapsApiKey);

const containerStyle = { position: "relative", maxWidth: "79.5vw", width: "100%", height: "calc(50vh - 1.75rem)" };
const mapContainerStyle = { width: "100%", height: "100%", borderRadius: "0rem 0rem 0.5rem 0.5rem" };

const MapDialogue = ({ selectedAddress: selected_address, propertyAddress, onSave = (address) => {}, onClose = () => {} }) => {
     const [selectedAddress, setSelectedAddress] = useState(selected_address);
     const [selectedLatLng, setSelectedLatLng] = useState(null);
     const [defaultCenter, setDefaultCenter] = useState({});

     const { isLoaded, loadError } = useJsApiLoader({
          id: "google-map-script",
          libraries: ["places"],
          googleMapsApiKey,
     });

     useEffect(() => {
          setDefaultCenter({});

          (async () => {
               if (!isLoaded) return;
               try {
                    // viewUtil.showSystemModalSpinner(translator.getLabel("loading_geo_coordinates"));

                    if (!stringUtil.isStringNullOrEmpty(selected_address)) {
                         // Try to get the missing geo coordinates if possible
                         const results = await getGeocode({ address: selected_address });
                         const { lat, lng } = getLatLng(results[0]);
                         setSelectedLatLng({ lat, lng });
                    } else if (!stringUtil.isStringNullOrEmpty(propertyAddress)) {
                         const results = await getGeocode({ address: propertyAddress });
                         const { lat, lng } = getLatLng(results[0]);
                         setDefaultCenter({ lat, lng });
                    }
               } catch (error) {
                    console.log("useEffect Error:", error.toString());
               } finally {
                    // viewUtil.closeModalSpinner();
               }
          })();
     }, [selected_address, propertyAddress, isLoaded]);

     return (
          <Modal isOpen={true} size="lg" animation={false} fade={false} style={containerStyle} contentClassName="h-100">
               <div className="d-flex flex-row align-items-center p-3 border-bottom">
                    <div className="window-title">{translator.getLabel("select_address")}</div>
                    <div className="d-flex flex-fill justify-content-end gap-2">
                         <Button className="btn-cancel" disabled={!isLoaded} onClick={onClose}>
                              {translator.getLabel("cancel")}
                         </Button>
                         <Button
                              className="btn-ok"
                              disabled={!isLoaded}
                              onClick={() => {
                                   if (stringUtil.isStringNullOrEmpty(selectedAddress)) {
                                        viewUtil.showErrorAlert(translator.getLabel("please_enter_address"));
                                        return;
                                   }
                                   onSave(selectedAddress, selectedLatLng);
                                   onClose();
                              }}
                         >
                              {translator.getLabel("done")}
                         </Button>
                    </div>
               </div>
               <ModalBody className="p-0 h-100">
                    {loadError != null ? (
                         <ErrorMessage message={loadError} />
                    ) : !isLoaded ? null : (
                         // <Spinner modalSpinnerDialogue={translator.getLabel("loading_map")} />
                         <MapWithPlacesComponent
                              selectedAddress={selectedAddress}
                              selectedLatLng={selectedLatLng}
                              defaultCenter={defaultCenter}
                              setSelectedAddress={setSelectedAddress}
                              setSelectedLatLng={setSelectedLatLng}
                         />
                    )}
               </ModalBody>
          </Modal>
     );
};

export default memo(MapDialogue);

function MapWithPlacesComponent(props) {
     const { selectedAddress, selectedLatLng, defaultCenter, setSelectedAddress, setSelectedLatLng } = props;

     const center = getMapCenterCoordinates(selectedLatLng, defaultCenter);

     const onMapClicked = async (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          setSelectedLatLng({ lat, lng });

          try {
               // viewUtil.showSystemModalSpinner(translator.getLabel("loading_geo_coordinates_from_address"));
               const response = await Geocode.fromLatLng(lat, lng);
               const address = response.results[0].formatted_address;
               setSelectedAddress(address);
          } catch (error) {
               setSelectedLatLng(null);
               setSelectedAddress(null);
          } finally {
               // viewUtil.closeModalSpinner();
          }
     };

     return (
          <div className="position-relative d-flex flex-column h-100">
               <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={center}
                    zoom={15}
                    onClick={onMapClicked}
                    options={{
                         streetViewControl: false,
                         mapTypeControl: false,
                         fullscreenControl: false,
                         minZoom: 4,
                         maxZoom: 20,
                    }}
               >
                    {selectedLatLng && <MarkerF position={selectedLatLng} />}
               </GoogleMap>

               <div
                    className="d-flex flex-column justify-content-between position-absolute top-0 "
                    style={{
                         left: "50%",
                         transform: "translate(-50%, 0)",
                         maxWidth: "60%",
                         width: "100%",
                         zIndex: "99",
                         pointerEvents: "none",
                    }}
               >
                    <PlacesAutocomplete
                         selectedAddress={selectedAddress}
                         selectedLatLng={selectedLatLng}
                         setSelectedAddress={setSelectedAddress}
                         setSelectedLatLng={setSelectedLatLng}
                    />
               </div>
          </div>
     );
}

function PlacesAutocomplete(props) {
     const { selectedAddress, selectedLatLng, setSelectedAddress, setSelectedLatLng } = props;

     const [ukAddressSuggestions, setUKAddressSuggestions] = useState(null);
     const [currentFetchId, setCurrentFetchId] = useState(null);

     const {
          ready,
          setValue,
          suggestions: { status, data },
          clearSuggestions,
     } = usePlacesAutocomplete();

     const handleSelect = async (address) => {
          setCurrentFetchId(null);
          setValue(address, false);
          setSelectedAddress(address);
          clearSuggestions();
          setUKAddressSuggestions(null);

          try {
               // viewUtil.showSystemModalSpinner(translator.getLabel("loading_geo_coordinates_from_address"));
               const results = await getGeocode({ address });
               const { lat, lng } = getLatLng(results[0]);
               setSelectedLatLng({ lat, lng });
          } catch (error) {
               setSelectedLatLng(null);
          } finally {
               // viewUtil.closeModalSpinner();
          }
     };

     const handleChange = (value) => {
          const address = value;
          const length = address.length;

          setSelectedAddress(address);
          setSelectedLatLng(null);

          if (length < 5) {
               setUKAddressSuggestions(null);
               setValue(null);
               clearSuggestions();
               return;
          }

          if (length < 9 && stringUtil.areStringSame(localizationUtils.getCountry(), "UK")) {
               if (address.split(" ").length - 1 < 2) {
                    setValue(null);
                    clearSuggestions();
                    setCurrentFetchId(guidUtil.generateGuid());
                    fetchUKAddressSuggestions(address);
                    return;
               }
          }

          setValue(address);
     };

     const fetchUKAddressSuggestions = async (postCode) => {
          try {
               const fetchId = currentFetchId;

               // viewUtil.showSystemModalSpinner("Attempting to fetch UK addresses by post code...");

               setUKAddressSuggestions(null);
               postCode = stringUtil.replaceAll(postCode, /[^a-zA-Z0-9 -]/, "");
               const resource = `core/services/geo/addressLookup/${postCode}`;

               const possibleAddresses = await rmsApiProxyRetry.put(resource);

               if (fetchId === currentFetchId && possibleAddresses.length > 0) {
                    setUKAddressSuggestions(possibleAddresses);
               }
          } catch (error) {
               console.log("fetchUKAddressSuggestions:", error.toString());
               return null;
          }
     };

     return (
          <div className="mt-3 shadow-lg" style={{ pointerEvents: "auto", width: "100%" }}>
               <InputTextComponent
                    inputmode="none"
                    type="text"
                    autoComplete="off"
                    value={selectedAddress}
                    placeholder={translator.getLabel("search_an_address")}
                    onValueChange={handleChange}
                    disabled={!ready}
                    className="border-2 border-dark border-opacity-25"
                    inputTextType="text"
               />
               <SuggestionList status={status} data={data} handleSelect={handleSelect} ukAddressSuggestions={ukAddressSuggestions} />
          </div>
     );
}

function SuggestionList(props) {
     const { status, data, ukAddressSuggestions, handleSelect } = props;

     const possibleSuggestions = [];

     if (ukAddressSuggestions != null && ukAddressSuggestions.length > 0) {
          // handle uk service suggestions
          ukAddressSuggestions.forEach((suggestion) => possibleSuggestions.push({ place_id: suggestion, description: suggestion }));
     } else if (status === "OK" || data.length > 0) {
          // handle google service suggestions
          data.forEach((item) => possibleSuggestions.push({ place_id: item.place_id, description: item.description }));
     }

     if (possibleSuggestions.length === 0) return null;

     const components = [];

     possibleSuggestions.forEach(({ place_id, description }, index) => {
          const backgroundColor = index % 2 ? "white" : "#F0F0F0";

          components.push(
               <div
                    key={place_id}
                    className="p-3 border-top"
                    style={{ background: backgroundColor, cursor: "pointer" }}
                    onClick={() => handleSelect(description)}
               >
                    {description}
               </div>
          );
     });

     return (
          <div className="border" style={{ maxHeight: "25vh", overflowY: "auto" }}>
               {components}
          </div>
     );
}

function getMapCenterCoordinates(selectedLatLng, defaultCenter) {
     const defCenter = {
          latitude: defaultCenter.lat || 53.2761532,
          longitude: defaultCenter.lng || -6.2181159,
     };

     const center = {
          lat: selectedLatLng != null ? selectedLatLng.lat : defCenter.latitude,
          lng: selectedLatLng != null ? selectedLatLng.lng : defCenter.longitude,
     };

     return center;
}
